/**
 * Phase INV-CAT (Gap 1) — Customer-facing attachments block on the
 * invoice detail page.
 *
 * Backend contract:
 *   GET /api/customer/invoices/{id}                returns attachments[]
 *      with {id, filename, content_type, byte_count, uploaded_at,
 *      uploaded_by, line_item_id, label}.   ``file_path`` is stripped
 *      server-side and must NEVER be referenced here.
 *   GET /api/customer/invoices/{id}/attachments/{att_id}/file
 *      streams the file (ownership enforced server-side).
 *
 * Behaviour:
 *   * If ``attachments`` is empty/null, render nothing (parent decides
 *     spacing).
 *   * Otherwise, render a card with one row per attachment showing
 *     filename · size · uploaded date · optional label.
 *   * Each row has a "Download" button that fetches the file via the
 *     auth-bearing ``api`` client as a blob and triggers a save dialog
 *     (same pattern as the COI/ACH download flows).
 */
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Paperclip, Download, Loader2 } from "lucide-react";

function fmtSize(bytes) {
    if (bytes == null) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function fmtDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

export default function CustomerInvoiceAttachments({ invoiceId, attachments }) {
    const [downloading, setDownloading] = useState({});

    if (!attachments || attachments.length === 0) return null;

    const download = async (att) => {
        if (downloading[att.id]) return;
        setDownloading((s) => ({ ...s, [att.id]: true }));
        try {
            const res = await api.get(
                `/customer/invoices/${invoiceId}/attachments/${att.id}/file`,
                { responseType: "blob" },
            );
            const blob = new Blob([res.data], { type: att.content_type || "application/octet-stream" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = att.filename;
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setDownloading((s) => {
                const next = { ...s };
                delete next[att.id];
                return next;
            });
        }
    };

    return (
        <Card data-testid="customer-invoice-attachments-card">
            <CardHeader>
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Paperclip size={16} /> Attached files
                    <span className="text-xs font-normal text-slate-500">
                        ({attachments.length})
                    </span>
                </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
                <ul className="divide-y divide-slate-100"
                    data-testid="customer-invoice-attachments-list">
                    {attachments.map((att) => (
                        <li key={att.id}
                            data-testid={`customer-invoice-attachment-row-${att.id}`}
                            className="flex items-center justify-between gap-3 px-4 py-3">
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-slate-900 truncate"
                                   data-testid={`customer-invoice-attachment-filename-${att.id}`}>
                                    {att.filename}
                                </p>
                                <p className="text-xs text-slate-500 mt-0.5 tabular-nums">
                                    {fmtSize(att.byte_count)} · uploaded {fmtDate(att.uploaded_at)}
                                    {att.label && (
                                        <span className="ml-1 inline-flex items-center rounded-full bg-blue-50 text-blue-900 ring-1 ring-blue-200 px-1.5 text-[10px] font-semibold"
                                              data-testid={`customer-invoice-attachment-label-${att.id}`}>
                                            {att.label}
                                        </span>
                                    )}
                                </p>
                            </div>
                            <Button
                                onClick={() => download(att)}
                                variant="outline"
                                size="sm"
                                disabled={!!downloading[att.id]}
                                className="gap-1.5 shrink-0"
                                data-testid={`customer-invoice-attachment-download-${att.id}`}
                            >
                                {downloading[att.id]
                                    ? <Loader2 size={13} className="animate-spin" />
                                    : <Download size={13} />}
                                Download
                            </Button>
                        </li>
                    ))}
                </ul>
            </CardContent>
        </Card>
    );
}
